import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RegisterContext } from "@profile/contexts/RegisterContext";
import { getRegisterFeedbackEntryNavigation } from "@profile/components/registration-wizard/feedback/register-feedback.navigation";
import { TextField, Button, cn } from "@fmc-digital/ui";
import { registerStepFormStyles } from "@profile/styles/register-flow.styles";

const smsVerificationConsentText =
  "I agree to receive an SMS text verification of my phone number with a one-time verification code from Freedom Mortgage to proceed with the verification phone number. Message and data charges may apply. One message sent per request.";

// Simple phone mask: (XXX) XXX-XXXX
function maskPhone(value: string) {
  const digits = value.replace(/\D/g, "");
  const regex = /(\d{0,3})(\d{0,3})(\d{0,4})/;
  const match = regex.exec(digits);
  if (!match) return "";
  let masked = "";
  if (match[1]) masked += `(${match[1]}`;
  if (match[2]) masked += match[2].length ? `) ${match[2]}` : "";
  if (match[3]) masked += match[3].length ? `-${match[3]}` : "";
  return masked;
}

function isRepeatedPattern(digits: string) {
  if (digits.length !== 10) return false;

  for (let patternLength = 1; patternLength <= digits.length / 2; patternLength += 1) {
    if (digits.length % patternLength !== 0) continue;
    const pattern = digits.slice(0, patternLength);
    const repeated = pattern.repeat(digits.length / patternLength);
    if (repeated === digits) return true;
  }

  return false;
}

function isValidPhoneNumber(phone: string) {
  const digits = phone.replace(/\D/g, "").slice(0, 10);
  const maskedPhone = maskPhone(digits);
  const hasValidFormat = /^\(\d{3}\) \d{3}-\d{4}$/.test(maskedPhone);
  if (!hasValidFormat) return false;
  return !isRepeatedPattern(digits);
}


interface PhoneStepProps {
  setStatusMessage: (msg: string | null) => void;
}

export function PhoneStep({ setStatusMessage }: PhoneStepProps) {
  const navigate = useNavigate();
  const { email, phone, setPhone, goNext, userUUID, personalInfo } = useContext(RegisterContext);
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [smsConsentChecked, setSmsConsentChecked] = useState(false);

  const isValidPhone = isValidPhoneNumber(phone);
  const phoneError = phoneTouched && !isValidPhone ? "Enter a valid mobile number." : undefined;

  const consentBodyStyle = {
    color: "var(--Schemes-On-Surface-Variant, #44474C)",
    fontFamily: "Poppins",
    fontSize: "12px",
    fontStyle: "normal" as const,
    fontWeight: 400,
    lineHeight: "16px",
    letterSpacing: "0.4px",
  };

  const consentErrorStyle = {
    color: "var(--Schemes-Error, #B42528)",
    fontFamily: "var(--Font-Family-body, Poppins)",
    fontSize: "var(--Font-Size-xs, 13px)",
    fontStyle: "normal" as const,
    fontWeight: 400,
    lineHeight: "normal",
  };

  const checkboxContainerStyle = {
    position: "relative" as const,
    display: "flex",
    width: "36px",
    height: "36px",
    padding: "4px",
    flexDirection: "column" as const,
    justifyContent: "center",
    alignItems: "center",
    aspectRatio: "1 / 1",
    flexShrink: 0,
  };

  const checkboxInputStyle = {
    position: "absolute" as const,
    inset: 0,
    margin: 0,
    opacity: 0,
    cursor: "pointer",
  };

  const checkboxVisualStyle = {
    width: "24px",
    height: "24px",
    borderRadius: "4px",
    border: "1px solid var(--Freedom-M3-sys-dark-on-surface-variant, #C4C6CC)",
    background: smsConsentChecked ? "#003049" : "transparent",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    pointerEvents: "none" as const,
  };

  const consentErrorAlignStyle = {
    marginLeft: "44px",
  };

  const handlePhoneChange = (value: string) => {
    const digits = value.replace(/\D/g, "").slice(0, 10);
    setPhone(digits);
    setPhoneTouched(true);
    setStatusMessage(null);
  };

  const handleNext = () => {
    setPhoneTouched(true);
    setStatusMessage(null);
    if (!isValidPhone || !smsConsentChecked) {
      return;
    }
    goNext();
  };

  return (
    <div className={cn(registerStepFormStyles(), "md:max-w-md md:mx-auto")}>
      <TextField
        label="Mobile number"
        value={maskPhone(phone)}
        onChange={e => handlePhoneChange(e.target.value)}
        onBlur={() => setPhoneTouched(true)}
        type="tel"
        fullWidth
        placeholder="Mobile number"
        maxLength={14}
        error={phoneError}
      />
      <div className="flex flex-col gap-1">
        <label className="flex items-start gap-2">
          <span style={checkboxContainerStyle}>
            <input
              type="checkbox"
              aria-label="SMS phone verification consent"
              checked={smsConsentChecked}
              style={checkboxInputStyle}
              onChange={(event) => {
                setSmsConsentChecked(event.target.checked);
              }}
            />
            <span style={checkboxVisualStyle}>
              {smsConsentChecked && (
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M10 16.4L6 12.4L7.4 11L10 13.6L16.6 7L18 8.4L10 16.4Z" fill="white"/>
                </svg>
              )}
            </span>
          </span>
          <span style={consentBodyStyle}>{smsVerificationConsentText}</span>
        </label>
        {!smsConsentChecked && (
          <span style={{ ...consentErrorStyle, ...consentErrorAlignStyle }}>
            *Please check this box to continue.
          </span>
        )}
      </div>
      <div className="flex flex-col gap-2 mt-4">
        <Button
          variant="filled"
          fullWidth
          onClick={handleNext}
          disabled={!isValidPhone || !smsConsentChecked}
        >
          Next
        </Button>
        <Button
          variant="outlined"
          fullWidth
          className="rounded-[100px] border-transparent"
          onClick={() => {
            setStatusMessage(null);
            const { route, state } = getRegisterFeedbackEntryNavigation({
              email,
              userUUID,
              loanNumber: personalInfo.loanNumber,
            });
            navigate(route, state ? { state } : undefined);
          }}
        >
          Skip
        </Button>
      </div>
    </div>
  );
}
